<script setup lang="ts">
import { auth } from '../../firebase'
import { signOut, type User } from 'firebase/auth'
import { computed, ref } from 'vue'
import { useUserStore } from '../../stores/userStore'
import { useProfileStore } from '../../stores/profileStore'
import NotificationCenter from './NotificationCenter.vue'
import RecentItems from './RecentItems.vue'

withDefaults(defineProps<{
  user: User | null
  showRecent?: boolean
}>(), {
  showRecent: true
})

const emit = defineEmits(['accepted', 'open-profile', 'navigate-to-song', 'navigate-to-mix', 'navigate-to-band'])

const userStore = useUserStore()
const profileStore = useProfileStore()
const displayName = computed(() => profileStore.profile?.nickname || userStore.dbUser?.displayName || userStore.user?.displayName || 'Musician')

const showLogoutConfirm = ref(false)

const handleAccepted = (bandName: string) => {
  emit('accepted', bandName)
}

const logout = async () => {
  try {
    await signOut(auth)
    localStorage.removeItem('pendingInviteToken')
    window.location.reload()
  } catch (error) {
    console.error('Error signing out:', error)
  }
}
</script>

<template>
  <div v-if="user">
    <div class="flex items-center justify-between pb-3 pt-2 px-3 lg:px-6 bg-slate-100 border-b border-slate-300">
      <div class="flex items-center gap-3">
        <img v-if="profileStore.profile?.avatarUrl" :src="profileStore.profile.avatarUrl" class="w-10 h-10 rounded-full object-cover" @error="() => { if (profileStore.profile) profileStore.profile.avatarUrl = undefined }" />
        <Avatar v-else :label="displayName.charAt(0)" size="large" shape="circle" severity="primary" />
        <div>
          <h1 class="text-lg font-bold text-slate-900 leading-tight italic">Hey, {{ displayName.split(' ')[0] }}!</h1>
          <p class="text-xs text-slate-500">{{ user.email }}</p>
        </div>
      </div>
      <div class="flex items-center gap-0 sm:gap-2">
        <Button severity="secondary" text rounded class="p-2 group hover:bg-slate-100 transition-all duration-200" v-tooltip.bottom="'Profile'" @click="emit('open-profile')">
          <i class="pi pi-cog text-xl text-slate-400 group-hover:text-slate-900 transition-colors duration-200"></i>
        </Button>
        <NotificationCenter @accepted="handleAccepted" @navigate-to-song="(bandId: number, songId: number) => emit('navigate-to-song', bandId, songId)" @navigate-to-band="(bandId: number) => emit('navigate-to-band', bandId)" />
        <Button :label="undefined" icon="pi pi-sign-out" severity="danger" text size="small" rounded v-tooltip.bottom="'Logout'" class="sm:!hidden" @click="showLogoutConfirm = true" />
        <Button label="Logout" icon="pi pi-sign-out" severity="danger" text size="small" class="!hidden sm:!inline-flex" @click="showLogoutConfirm = true" />
      </div>
    </div>

    <RecentItems
      v-if="showRecent"
      @navigate-to-song="(bandId: number, songId: number) => emit('navigate-to-song', bandId, songId)"
      @navigate-to-mix="(bandId: number, songId: number, mixId: number) => emit('navigate-to-mix', bandId, songId, mixId)"
    />

    <!-- Logout Confirmation Dialog -->
    <Dialog v-model:visible="showLogoutConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-amber-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-amber-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Are you sure?</h2>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Logout" severity="danger" class="w-full py-3 font-bold" @click="logout" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showLogoutConfirm = false" />
        </div>
      </div>
    </Dialog>
  </div>
</template>
